import { useTheme } from "@mui/material";
import {
  MenuButtonAddTable,
  MenuButtonBlockquote,
  MenuButtonBold,
  MenuButtonBulletedList,
  MenuButtonCode,
  MenuButtonCodeBlock,
  MenuButtonEditLink,
  MenuButtonHighlightColor,
  MenuButtonHorizontalRule,
  MenuButtonImageUpload,
  MenuButtonIndent,
  MenuButtonItalic,
  MenuButtonOrderedList,
  MenuButtonRedo,
  MenuButtonRemoveFormatting,
  MenuButtonStrikethrough,
  MenuButtonSubscript,
  MenuButtonSuperscript,
  MenuButtonTaskList,
  MenuButtonTextColor,
  MenuButtonUnderline,
  MenuButtonUndo,
  MenuButtonUnindent,
  MenuControlsContainer,
  MenuDivider,
  MenuSelectFontFamily,
  MenuSelectFontSize,
  MenuSelectHeading,
  MenuSelectTextAlign,
  isTouchDevice,
  MenuButton,
  useRichTextEditorContext,
} from "mui-tiptap";
import { Image, VideoLibrary, TextFields, Lock, LockOpen } from "@mui/icons-material";
import { useCallback } from "react";
import { insertImages } from "mui-tiptap";
import { Button } from "@mui/material";

export default function EditorMenuControls({
  handleSubmit,
  showPreview,
  setShowPreview,
}: {
  handleSubmit: () => void;
  showPreview?: boolean;
  setShowPreview?: (v: boolean) => void;
}) {
  const theme = useTheme();
  const editor = useRichTextEditorContext();

  const handleInsertImageUrl = useCallback(() => {
    const url = prompt("Enter image URL:");
    if (url && editor) {
      // Ensure the URL is properly formatted
      let imageUrl = url.trim();

      // If the URL is relative, make it absolute
      if (imageUrl.startsWith("/")) {
        imageUrl = `${window.location.origin}${imageUrl}`;
      }

      // If the URL is from the same domain, use the asset path
      if (imageUrl.startsWith(window.location.origin)) {
        imageUrl = imageUrl.replace(window.location.origin, "");
      }

      insertImages({
        images: [{ src: imageUrl, alt: "Inserted image" }],
        editor: editor,
      });
    }
  }, [editor]);

  const handleInsertYoutubeVideo = useCallback(() => {
    const url = prompt("Enter YouTube video URL:");
    if (url && editor) {
      // Extract video ID from YouTube URL
      const videoId = url.match(
        /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/,
      )?.[1];
      if (videoId) {
        editor.commands.setIframe({
          src: `https://www.youtube-nocookie.com/embed/${videoId}`,
        });
      } else {
        alert("Invalid YouTube URL");
      }
    }
  }, [editor]);

  return (
    <MenuControlsContainer>
      <MenuButton
        value="formatting"
        tooltipLabel={editor?.isActive("bold") ? "Hide formatting" : "Show formatting"}
        size="small"
        onClick={() => editor?.commands.toggleBold()}
        selected={editor?.isActive("bold")}
        IconComponent={TextFields}
      />

      <MenuButton
        value="formatting"
        tooltipLabel={editor?.isEditable ? "Prevent edits (use read-only mode)" : "Allow edits"}
        size="small"
        onClick={() => editor?.setEditable(!editor.isEditable)}
        selected={!editor?.isEditable}
        IconComponent={editor?.isEditable ? Lock : LockOpen}
      />

      <MenuDivider />

      <MenuSelectFontFamily
        options={[
          { label: "Comic Sans", value: "Comic Sans MS, Comic Sans" },
          { label: "Cursive", value: "cursive" },
          { label: "Monospace", value: "monospace" },
          { label: "Serif", value: "serif" },
        ]}
      />

      <MenuDivider />

      <MenuSelectHeading />

      <MenuDivider />

      <MenuSelectFontSize />

      <MenuDivider />

      <MenuButtonBold />

      <MenuButtonItalic />

      <MenuButtonUnderline />

      <MenuButtonStrikethrough />

      <MenuButtonSubscript />

      <MenuButtonSuperscript />

      <MenuDivider />

      <MenuButtonTextColor
        defaultTextColor={theme.palette.text.primary}
        swatchColors={[
          { value: "#000000", label: "Black" },
          { value: "#ffffff", label: "White" },
          { value: "#888888", label: "Grey" },
          { value: "#ff0000", label: "Red" },
          { value: "#ff9900", label: "Orange" },
          { value: "#ffff00", label: "Yellow" },
          { value: "#00d000", label: "Green" },
          { value: "#0000ff", label: "Blue" },
        ]}
      />

      <MenuButtonHighlightColor
        swatchColors={[
          { value: "#595959", label: "Dark grey" },
          { value: "#dddddd", label: "Light grey" },
          { value: "#ffa6a6", label: "Light red" },
          { value: "#ffd699", label: "Light orange" },
          { value: "#ffff00", label: "Yellow" },
          { value: "#99cc99", label: "Light green" },
          { value: "#90c6ff", label: "Light blue" },
          { value: "#8085e9", label: "Light purple" },
        ]}
      />

      <MenuDivider />

      <MenuButtonEditLink />

      <MenuDivider />

      <MenuSelectTextAlign />

      <MenuDivider />

      <MenuButtonOrderedList />

      <MenuButtonBulletedList />

      <MenuButtonTaskList />

      {/* On touch devices, we'll show indent/unindent buttons, since they're
      unlikely to have a keyboard that will allow for using Tab/Shift+Tab. These
      buttons probably aren't necessary for keyboard users and would add extra
      clutter. */}
      {isTouchDevice() && (
        <>
          <MenuButtonIndent />

          <MenuButtonUnindent />
        </>
      )}

      <MenuDivider />

      <MenuButtonBlockquote />

      <MenuDivider />

      <MenuButtonCode />

      <MenuButtonCodeBlock />

      <MenuDivider />

      {/* <MenuButtonImageUpload
        onUploadFiles={(files) =>
          // For the sake of a demo, we don't have a server to upload the files
          // to, so we'll instead convert each one to a local "temporary" object
          // URL. This will not persist properly in a production setting. You
          // should instead upload the image files to your server, or perhaps
          // convert the images to bas64 if you would like to encode the image
          // data directly into the editor content, though that can make the
          // editor content very large.
          files.map((file) => ({
            src: URL.createObjectURL(file),
            alt: file.name,
          }))
        }
      /> */}

      <MenuButton
        value="image-url"
        tooltipLabel="Insert image from URL"
        size="small"
        onClick={handleInsertImageUrl}
        IconComponent={Image}
      />

      <MenuButton
        value="youtube"
        tooltipLabel="Insert YouTube video"
        size="small"
        onClick={handleInsertYoutubeVideo}
        IconComponent={VideoLibrary}
      />

      <MenuDivider />

      <MenuButtonHorizontalRule />

      <MenuButtonAddTable />

      <MenuDivider />

      <MenuButtonRemoveFormatting />

      <MenuDivider />

      <MenuButtonUndo />
      <MenuButtonRedo />

      <MenuDivider />

      <Button variant="contained" size="small" onClick={handleSubmit}>
        Xác nhận
      </Button>
    </MenuControlsContainer>
  );
}
